import React from "react";
import { Marker } from "@react-google-maps/api";
import "../../../assets/css/GoogleMaps.css";

/** On-map pixel size for red asset teardrop icons (native SVG 32×50, scaled for readability). */
export const ASSET_PIN_DISPLAY_WIDTH = 27;
export const ASSET_PIN_DISPLAY_HEIGHT = 42;

/**
 * Utility function to create a marker icon configuration
 */
export const createMarkerIcon = (
  url: string = "/map-marker.svg",
  size: number = 40, // Increased from 32 to 40
  anchorX?: number,
  anchorY?: number,
): google.maps.Icon => {
  return {
    url,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(anchorX || size / 2, anchorY || size),
  };
};

/**
 * Generates an SVG data URL for AssetEmpty.svg with a number displayed inside
 * @param assetId - The asset ID/number to display in the marker
 * @returns A data URL string that can be used as a marker icon
 */
export const createAssetMarkerWithNumber = async (
  assetId: number | string | undefined,
): Promise<string> => {
  if (assetId === undefined || assetId === null) {
    // Return default AssetEmpty.svg if no ID provided
    return "/AssetEmpty.svg";
  }

  try {
    // Fetch the base SVG
    const response = await fetch("/AssetEmpty.svg");
    const svgText = await response.text();

    // Parse the SVG and add text element for the number
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
    const svgElement = svgDoc.documentElement;

    // Create a text element for the number
    const textElement = svgDoc.createElementNS(
      "http://www.w3.org/2000/svg",
      "text",
    );
    textElement.setAttribute("x", "16");
    textElement.setAttribute("y", "18");
    textElement.setAttribute("text-anchor", "middle");
    textElement.setAttribute("dominant-baseline", "middle");
    textElement.setAttribute("fill", "white");
    textElement.setAttribute("font-family", "Arial, sans-serif");
    textElement.setAttribute("font-size", "16");
    textElement.setAttribute("font-weight", "bold");
    textElement.textContent = String(assetId);

    // Insert the text before the closing </g> tag (inside the main group)
    const mainGroup = svgElement.querySelector("g[clip-path]");
    if (mainGroup) {
      mainGroup.appendChild(textElement);
    } else {
      // Fallback: append to svg root
      svgElement.appendChild(textElement);
    }

    // Convert back to string
    const serializer = new XMLSerializer();
    const modifiedSvg = serializer.serializeToString(svgElement);

    // Create data URL
    const blob = new Blob([modifiedSvg], { type: "image/svg+xml" });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error creating asset marker with number:", error);
    // Fallback to default
    return "/AssetEmpty.svg";
  }
};

/**
 * Synchronous version that creates SVG data URL inline (for immediate use)
 * @param assetNumber - The number to display in the marker (1-indexed)
 * @returns A data URL string that can be used as a marker icon
 */
export const createAssetMarkerWithNumberSync = (
  assetNumber: number | string | undefined,
): string => {
  if (assetNumber === undefined || assetNumber === null) {
    return "/AssetEmpty.svg";
  }

  // Base SVG structure from AssetEmpty.svg
  const svgContent = `<svg width="32" height="50" viewBox="0 0 32 50" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_277_22299)">
    <g filter="url(#filter0_f_277_22299)">
      <ellipse cx="16.0149" cy="41.4927" rx="5.82353" ry="2.18382" fill="#38393E"/>
    </g>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M32.0294 15.4992C32.0294 21.3734 28.0188 27.453 24.653 31.5181C23.1871 33.2893 17.4316 39.3199 16.0114 39.3088C14.5864 39.2993 8.83902 33.2861 7.37646 31.5181C4.01061 27.453 0 21.3734 0 15.4992C0 6.9533 7.18385 0 16.0147 0C24.8456 0 32.0294 6.9533 32.0294 15.4992Z" fill="url(#paint0_linear_277_22299)"/>
    <text x="16" y="20" text-anchor="middle" dominant-baseline="middle" fill="white" font-family="Arial, sans-serif" font-size="16" font-weight="bold">${assetNumber}</text>
  </g>
  <defs>
    <filter id="filter0_f_277_22299" x="2.03656" y="31.154" width="27.9567" height="20.6774" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
      <feGaussianBlur stdDeviation="4.07742" result="effect1_foregroundBlur_277_22299"/>
    </filter>
    <linearGradient id="paint0_linear_277_22299" x1="3.93859" y1="9.66744" x2="3.93859" y2="39.3088" gradientUnits="userSpaceOnUse">
      <stop stop-color="#800000"/>
      <stop offset="1" stop-color="#FF2C2C"/>
    </linearGradient>
    <clipPath id="clip0_277_22299">
      <rect width="32" height="50" fill="white"/>
    </clipPath>
  </defs>
</svg>`;

  // Create data URL
  const encodedSvg = encodeURIComponent(svgContent);
  return `data:image/svg+xml,${encodedSvg}`;
};

export interface MapMarkerProps {
  latitude: number;
  longitude: number;
  title?: string;
  assetName?: string;
  assetId?: number | string;
  assetIndex?: number; // 1-indexed position for numbering (takes precedence over assetId if provided)
  draggable?: boolean;
  icon?: string | google.maps.Icon | google.maps.Symbol;
  zIndex?: number;
  clusterer?: any;
  onMarkerClick?: (event: google.maps.MapMouseEvent) => void;
  onDragEnd?: (event: google.maps.MapMouseEvent) => void;
}

export interface MultipleMarkersProps {
  markers: Array<{
    latitude: number;
    longitude: number;
    title?: string;
    icon?: string | google.maps.Icon | google.maps.Symbol;
    id?: string | number;
    assetId?: number | string;
    assetIndex?: number; // 1-indexed position for numbering (takes precedence over assetId if provided)
    onMarkerClick?: (event: google.maps.MapMouseEvent) => void;
  }>;
  defaultIcon?: string;
  useAssetNumbers?: boolean; // If true, use AssetEmpty.svg with numbers for markers with assetIndex or assetId
}

/**
 * Single marker component for assets or individual locations
 */
export const MapMarker: React.FC<MapMarkerProps> = ({
  latitude,
  longitude,
  title,
  assetName,
  assetId,
  assetIndex,
  draggable = false,
  icon,
  zIndex = 9999,
  clusterer,
  onMarkerClick,
  onDragEnd,
}) => {
  const markerTitle = title || assetName || "Asset Location";

  const returnIcon = (number: number) => {
    const svgDataUrl = createAssetMarkerWithNumberSync(number);
    return {
      url: svgDataUrl,
      scaledSize: new window.google.maps.Size(
        ASSET_PIN_DISPLAY_WIDTH,
        ASSET_PIN_DISPLAY_HEIGHT,
      ),
      anchor: new window.google.maps.Point(
        ASSET_PIN_DISPLAY_WIDTH / 2,
        ASSET_PIN_DISPLAY_HEIGHT,
      ),
    };
  };

  // Create custom icon - use AssetEmpty with number if assetIndex or assetId is provided and no custom icon
  const markerIcon = React.useMemo(() => {
    if (icon) {
      return typeof icon === "string" ? createMarkerIcon(icon, 40) : icon;
    }

    // Prefer assetIndex (0-indexed) over assetId if provided
    const numberToDisplay =
      assetIndex !== undefined && assetIndex !== null
        ? assetIndex
        : assetId !== undefined && assetId !== null
          ? assetId
          : undefined;

    if (numberToDisplay !== undefined && numberToDisplay !== null) {
      return returnIcon(numberToDisplay as number);
    }

    // Default fallback
    return createMarkerIcon("/map-marker.svg", 40);
  }, [icon, assetId, assetIndex]);

  return (
    <Marker
      position={{ lat: latitude, lng: longitude }}
      title={markerTitle}
      draggable={draggable}
      clusterer={clusterer}
      onDragEnd={onDragEnd}
      onClick={onMarkerClick}
      icon={markerIcon}
      zIndex={zIndex}
    />
  );
};

/**
 * Multiple markers component for rendering arrays of markers
 */
export const MapMarkers: React.FC<MultipleMarkersProps> = ({
  markers,
  defaultIcon = "/map-marker.svg",
  useAssetNumbers = false,
}) => {
  // Create default icon configuration - same as MapMarker
  const defaultIconConfig = createMarkerIcon(defaultIcon, 40);

  return (
    <>
      {markers.map((marker, index) => {
        // Determine icon to use
        let markerIcon: string | google.maps.Icon | google.maps.Symbol;

        if (marker.icon) {
          // Use custom icon if provided
          markerIcon =
            typeof marker.icon === "string"
              ? createMarkerIcon(marker.icon, 40)
              : marker.icon;
        } else if (useAssetNumbers) {
          // Prefer assetIndex (1-indexed) over assetId if provided
          const numberToDisplay =
            marker.assetIndex !== undefined && marker.assetIndex !== null
              ? marker.assetIndex
              : marker.assetId !== undefined && marker.assetId !== null
                ? marker.assetId
                : undefined;

          if (numberToDisplay !== undefined && numberToDisplay !== null) {
            // Use AssetEmpty.svg with number if assetIndex or assetId is provided and useAssetNumbers is true
            const svgDataUrl = createAssetMarkerWithNumberSync(numberToDisplay);
            // Same on-map size as single MapMarker asset pins
            markerIcon = {
              url: svgDataUrl,
              scaledSize: new window.google.maps.Size(
                ASSET_PIN_DISPLAY_WIDTH,
                ASSET_PIN_DISPLAY_HEIGHT,
              ),
              anchor: new window.google.maps.Point(
                ASSET_PIN_DISPLAY_WIDTH / 2,
                ASSET_PIN_DISPLAY_HEIGHT,
              ),
            };
          } else {
            // Use default icon
            markerIcon = defaultIconConfig;
          }
        } else {
          // Use default icon
          markerIcon = defaultIconConfig;
        }

        return (
          <Marker
            key={marker.id || `marker-${index}`}
            position={{ lat: marker.latitude, lng: marker.longitude }}
            title={marker.title || `Location ${index + 1}`}
            icon={markerIcon}
            onClick={marker.onMarkerClick}
          />
        );
      })}
    </>
  );
};
